import React, { useState, useCallback, useEffect } from "react";
import MaterialsTooltip from "./MaterialsTooltip";
import MaterialsDetailedTooltip from "./MaterialsDetailedTooltip";
import DeliveryTooltip from "./DeliveryTooltip";
import DaysTooltip from "./DaysTooltip";
import LogistRequestModal from "./LogistRequestModal";
import NomenclatureImagesModal from "./NomenclatureImagesModal";
import { CatLoaderInline } from "../../components/CatLoader";
import { getShortRegionName } from "../../projectProducts/regionUtils";
import { Image } from "react-bootstrap-icons";
import { isSelectedComparisonRow } from "./gazSelectedComparison";
import { getNomenclatureImageKey, getNomenclatureImageTarget } from "../../utils/nomenclatureImageTarget";
import GazComparisonTable from './GazComparisonTable';
import { buildGazComparisonView } from './gazComparisonView';

// Маппинг сокращенных названий на полные (как в regionUtils.js)
const REGION_SHORT_TO_FULL = {
  'СПБ': 'Санкт-Петербург',
  'МСК': 'Москва',
  'КРД': 'Краснодар',
};

const ProductionsTable = ({
  isValidInput,
  isLoading,
  sortedGroupedData,
  hasValidDescriptions,
  selfPickUp,
  handleSelectOffer,
  handleMouseEnter,
  handleMouseLeave,
  tooltip,
  tableContainerRef,
  tooltipRef,
  products,
  hasObshchestroyCalculator,
  shouldShowRequestLink,
  dealId,
  clientName,
  addressAndCoordinates,
  selectedRegion,
  // Признак разрешённой области и ключ сравнения, по которому собрано текущее КП.
  // Оба приходят готовыми сверху: в ячейках регион и категория не пересчитываются.
  isMskGazobetonScope = false,
  selectedComparisonId = null,
  // Форма оплаты и наличие допродаж нужны только таблице сравнения внутри области:
  // по первой считается показанная сумма к оплате, по второму — оговорка о том,
  // что допродажи в сравнение не входят. Вне области оба не используются.
  paymentMethod,
  hasGazUpsells = false,
  deliveryDate,
  isCatMode,
}) => {
  const [showTooltipOnFirstLoad, setShowTooltipOnFirstLoad] = useState(false);
  const [showLogistRequestModal, setShowLogistRequestModal] = useState(false);
  const [logistFormData, setLogistFormData] = useState({
    regionId: '',
    regionName: '',
    comment: '',
    items: []
  });
  const [logistFormErrors, setLogistFormErrors] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoadingLogistOptions, setIsLoadingLogistOptions] = useState(false);
  const [departmentTree, setDepartmentTree] = useState(null);
  const [flows, setFlows] = useState([]);
  const [imageModalData, setImageModalData] = useState({
    isOpen: false,
    imageUrls: [],
    title: "",
    isLoading: false,
  });
  const [imageAvailabilityMap, setImageAvailabilityMap] = useState({});
  const [paymentType, setPaymentType] = useState('Нал'); // Форма оплаты по воронке

  // Функция закрытия модального окна
  const handleCloseLogistRequestModal = () => {
    setShowLogistRequestModal(false);
  };

  const handleCloseImageModal = useCallback(() => {
    setImageModalData({
      isOpen: false,
      imageUrls: [],
      title: "",
      isLoading: false,
    });
  }, []);

  const getImageUrlsFromResponse = useCallback((result) => {
    if (Array.isArray(result?.imageUrls)) {
      return result.imageUrls.filter(Boolean);
    }

    if (Array.isArray(result?.images)) {
      return result.images
        .map((image) => (typeof image === "string" ? image : image?.imageUrl || image?.image_url || image?.url))
        .filter(Boolean);
    }

    return result?.imageUrl ? [result.imageUrl] : [];
  }, []);

  // Правило «чьё это фото» одно на обе таблицы — общую и газобетонную, поэтому
  // живёт отдельным модулем.
  //
  // Разница в строгости. Сервер с 12.10.2026 отдаёт общее фото поставщика и
  // строке без нашей позиции, но спрашивать его так начинает только газобетон:
  // вне области поведение обязано совпадать с прежним, где строка без guide_id
  // фото не запрашивала вовсе. Расширять это на общую таблицу — отдельное
  // решение, не наше.
  const getRowImagePayload = useCallback((entry) => {
    const target = getNomenclatureImageTarget(entry);

    if (!target || (!isMskGazobetonScope && !target.nomenclatureId)) {
      return null;
    }

    return target;
  }, [isMskGazobetonScope]);

  const getImageAvailabilityKey = useCallback(
    (entry) => (getRowImagePayload(entry) ? getNomenclatureImageKey(entry) : null),
    [getRowImagePayload]
  );

  useEffect(() => {
    const rows = Array.isArray(sortedGroupedData) ? sortedGroupedData : [];
    const payloads = rows
      .map((entry) => {
        const payload = getRowImagePayload(entry);

        return payload
          ? {
            key: getNomenclatureImageKey(entry),
            ...payload,
          }
          : null;
      })
      .filter(Boolean);

    if (payloads.length === 0) {
      setImageAvailabilityMap((prev) => (
        Object.keys(prev).length > 0 ? {} : prev
      ));
      return;
    }

    const uniquePayloads = Array.from(
      new Map(payloads.map((item) => [item.key, item])).values()
    );
    const missingPayloads = uniquePayloads.filter(
      ({ key }) => !(key in imageAvailabilityMap)
    );

    if (missingPayloads.length === 0) {
      return;
    }

    let isCancelled = false;

    Promise.all(
      missingPayloads.map(async ({ key, productionId, nomenclatureId }) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/nomenclature-image?productionId=${encodeURIComponent(productionId)}&nomenclatureId=${encodeURIComponent(nomenclatureId ?? "")}`,
            { credentials: "include" }
          );
          const result = await response.json();

          return [key, Boolean(result?.success && getImageUrlsFromResponse(result).length > 0)];
        } catch (error) {
          console.error("Ошибка проверки изображения номенклатуры:", error);
          return [key, false];
        }
      })
    ).then((entries) => {
      if (isCancelled) {
        return;
      }

      setImageAvailabilityMap((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    });

    return () => {
      isCancelled = true;
    };
  }, [sortedGroupedData, getImageUrlsFromResponse, getRowImagePayload, imageAvailabilityMap]);

  const handleOpenImageModal = useCallback(async (entry, event) => {
    event?.stopPropagation();

    const payload = getRowImagePayload(entry);
    if (!payload) {
      return;
    }

    setImageModalData({
      isOpen: true,
      imageUrls: [],
      title: entry?.comparison?.production || "",
      isLoading: true,
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/nomenclature-image?productionId=${encodeURIComponent(payload.productionId)}&nomenclatureId=${encodeURIComponent(payload.nomenclatureId ?? "")}`,
        { credentials: "include" }
      );
      const result = await response.json();

      setImageModalData({
        isOpen: true,
        imageUrls: result?.success ? getImageUrlsFromResponse(result) : [],
        title: entry?.comparison?.production || "",
        isLoading: false,
      });
    } catch (error) {
      console.error("Ошибка загрузки изображения номенклатуры:", error);
      setImageModalData({
        isOpen: true,
        imageUrls: [],
        title: entry?.comparison?.production || "",
        isLoading: false,
      });
    }
  }, [getImageUrlsFromResponse, getRowImagePayload]);

  // Функция создания entry из sortedGroupedData или products
  const createEntryFromData = () => {
    // Если есть sortedGroupedData с записями "Из справочника", используем их
    if (sortedGroupedData && sortedGroupedData.length > 0) {
      const entry = sortedGroupedData.find(e => e.comparison.production === "Из справочника");
      if (entry) return entry;
    }

    // Если есть products, создаем entry из них
    if (products && products.length > 0) {
      const materialsFromProducts = products.map(product => ({
        name: product.name || product.productName || 'Товар без названия',
        quantity: product.quantity || 1,
        pricePerOne: product.price || 0,
        calculator: product.calculator || '',
        category: product.category || '',
        source: product.source || ''
      }));

      return {
        comparison: { production: "Из справочника" },
        materialsToShow: materialsFromProducts
      };
    }

    // Fallback - пустая запись
    return {
      comparison: { production: "Из справочника" },
      materialsToShow: [{
        name: "Товары из справочника",
        quantity: 1,
        pricePerOne: 0,
        calculator: "",
        category: "",
        source: ""
      }]
    };
  };

  // Функция изменения полей в форме
  const handleItemChange = (itemId, field, value) => {
    setLogistFormData(prev => {
      const items = prev.items.map(item => {
        if (item.id === itemId) {
          return { ...item, [field]: value };
        }
        return item;
      });
      return { ...prev, items };
    });
  };

  // Функция валидации формы (как в ServicePage)
  const validateLogistForm = () => {
    const errors = {};
    if (!logistFormData.regionId) {
      errors.region = "Поле 'Регион' обязательно";
    }

    // Проверяем каждый блок товара
    logistFormData.items.forEach((item) => {
      if (!item.material) {
        errors[`${item.id}_material`] = "Поле 'Материал' обязательно";
      }
      if (!item.quantity) {
        errors[`${item.id}_quantity`] = "Поле 'Количество' обязательно";
      }
      if (!item.competitorPrice) {
        errors[`${item.id}_competitorPrice`] = "Поле обязательно";
      }
    });

    setLogistFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Функция загрузки потоков
  const loadFlows = useCallback(async () => {
    if (flows && flows.length > 0) {
      console.log('✅ Потоки уже загружены');
      return;
    }
    try {
      console.log('📥 Загружаем потоки...');
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/bitrix/flows`,
        { credentials: "include" }
      );
      const result = await response.json();
      if (result.success) {
        setFlows(result.flows || []);
        console.log('✅ Потоки загружены:', result.flows);
      } else {
        console.error('❌ Ошибка при загрузке потоков:', result.error);
        setFlows([{ id: 25, name: 'Общестрой СПБ', code: 'ОбщестройСПБ' }]);
      }
    } catch (err) {
      console.error('❌ Ошибка при загрузке потоков:', err);
      setFlows([{ id: 25, name: 'Общестрой СПБ', code: 'ОбщестройСПБ' }]);
    }
  }, [flows]);

  // Функция загрузки department-tree
  const loadDepartmentTree = useCallback(async () => {
    if (departmentTree) {
      console.log('✅ DepartmentTree уже загружен');
      return;
    }
    try {
      console.log('📥 Загружаем department-tree...');
      const cachedTree = localStorage.getItem('departmentTree_15');
      const cachedTimestamp = localStorage.getItem('departmentTree_15_timestamp');
      const CACHE_TTL = 10 * 60 * 1000; // 10 минут
      if (cachedTree && cachedTimestamp) {
        const isExpired = Date.now() - parseInt(cachedTimestamp) > CACHE_TTL;
        if (!isExpired) {
          try {
            const parsedTree = JSON.parse(cachedTree);
            console.log(`[loadDepartmentTree] Department-tree из кэша:`, {
              type: typeof parsedTree,
              isArray: Array.isArray(parsedTree),
              hasStructure: parsedTree?.structure,
              structureType: typeof parsedTree?.structure
            });
            if (parsedTree && !Array.isArray(parsedTree) && parsedTree.structure) {
              setDepartmentTree(parsedTree.structure);
              console.log('✅ Department-tree загружен из кэша (с извлечением structure)');
            } else if (Array.isArray(parsedTree)) {
              setDepartmentTree(parsedTree);
              console.log('✅ Department-tree загружен из кэша (массив)');
            } else if (parsedTree && parsedTree.ID) {
              setDepartmentTree([parsedTree]);
              console.log('⚠️ Department-tree из кэша был одним департаментом, обернут в массив');
            } else {
              setDepartmentTree(parsedTree);
              console.log('✅ Department-tree загружен из кэша');
            }
            return;
          } catch (err) {
            console.error('[loadDepartmentTree] Ошибка парсинга department-tree из кэша:', err);
            localStorage.removeItem('departmentTree_15');
            localStorage.removeItem('departmentTree_15_timestamp');
          }
        } else {
          console.log('⚠️ Кэш department-tree устарел, перезагружаем...');
          localStorage.removeItem('departmentTree_15');
          localStorage.removeItem('departmentTree_15_timestamp');
        }
      }
      const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/department-tree/15`, {
        credentials: "include",
      });
      const result = await response.json();
      if (result.success) {
        setDepartmentTree(result.structure);
        localStorage.setItem('departmentTree_15', JSON.stringify(result.structure));
        localStorage.setItem('departmentTree_15_timestamp', Date.now().toString());
        console.log('✅ Department-tree загружен и закэширован');
      } else {
        console.error('❌ Ошибка загрузки department-tree:', result.error);
      }
    } catch (err) {
      console.error('❌ Ошибка при загрузке department-tree:', err);
    }
  }, [departmentTree]);

  // Bitrix API URL
  const BITRIX_WEBHOOK_URL = process.env.REACT_APP_ROOF_BITRIX_CRM_DEAL_PRODUCTSROWS_SET;

  // Функция загрузки данных для dropdown'ов (из Bitrix API как в ServicePage)
  const fetchLogistDropdownOptions = useCallback(async () => {
    console.log('📥 Загружаем опции для dropdown\'ов логиста из Bitrix...');

    if (regionOptions.length > 0 && tagOptions.length > 0) {
      console.log('[fetchLogistDropdownOptions] Опции уже загружены, пропускаем');
      return true;
    }

    setIsLoadingLogistOptions(true);
    try {
      const response = await fetch(`${BITRIX_WEBHOOK_URL}/mlt.logistics.tags.list`);
      const data = await response.json();
      console.log('[fetchLogistDropdownOptions] Ответ от Bitrix:', data);

      if (data && data.result) {
        const { regions, tags } = data.result;

        if (regions) {
          setRegionOptions(regions.map(item => ({
            value: item.ID,
            label: item.NAME,
          })));
          console.log(`✅ Регионы загружены (${regions.length}):`, regions);
        }

        if (tags) {
          setTagOptions(tags.map(item => ({
            value: item.ID,
            label: item.NAME,
          })));
          console.log(`✅ Теги загружены (${tags.length}):`, tags);
        }

        return true;
      } else {
        throw new Error('Не удалось загрузить данные из Bitrix API');
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки опций из Bitrix:', error);
      // Fallback значения
      setRegionOptions([
        { value: "1", label: "СПБ" },
        { value: "2", label: "МСК" },
        { value: "3", label: "КРД" },
      ]);
      setTagOptions([
        { value: "1", label: "Общестрой" },
        { value: "2", label: "Кровля" },
        { value: "3", label: "Утеплитель" },
      ]);
      return false;
    } finally {
      setIsLoadingLogistOptions(false);
    }
  }, [regionOptions.length, tagOptions.length, BITRIX_WEBHOOK_URL]);

  // Функция предзагрузки данных
  const preloadLogistData = useCallback(async () => {
    console.log('🔄 preloadLogistData вызван');
    console.log('📊 Текущие regionOptions.length:', regionOptions.length);
    console.log('📊 Текущие tagOptions.length:', tagOptions.length);
    console.log('📊 Текущие flows.length:', flows?.length || 0);
    console.log('📊 DepartmentTree:', departmentTree);

    if (regionOptions.length > 0 && tagOptions.length > 0 && (flows?.length || 0) > 0 && departmentTree) {
      console.log('✅ Данные уже загружены, пропускаем');
      return;
    }

    try {
      setIsLoadingLogistOptions(true);
      await Promise.all([
        fetchLogistDropdownOptions(),
        loadFlows(),
        loadDepartmentTree()
      ]);
    } catch (error) {
      console.error('Ошибка при предзагрузке данных:', error);
    } finally {
      setIsLoadingLogistOptions(false);
    }
  }, [regionOptions.length, tagOptions.length, flows?.length, departmentTree, fetchLogistDropdownOptions, loadFlows, loadDepartmentTree]);

  // Функция получения возраста цены в днях (вспомогательная)
  const getPriceAgeInDays = (date) => {
    if (!date) return 0;
    const materialDate = new Date(date);
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - materialDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Функция определения тега по категории (вспомогательная)
  const getTagByCategory = (category) => {
    if (!category) return 'Общестрой';
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('бетон')) return 'Бетон';
    if (categoryLower.includes('газобетон')) return 'Газобетон';
    if (categoryLower.includes('кирпич')) return 'Кирпич';
    if (categoryLower.includes('кровля') || categoryLower.includes('профлист')) return 'Кровля';
    if (categoryLower.includes('металл')) return 'Металл';
    if (categoryLower.includes('щебень') || categoryLower.includes('песок') || categoryLower.includes('нерудка')) return 'Нерудка';
    return 'Общестрой';
  };

  // Функция получения категорий из Bitrix
  const fetchCategoriesFromBitrix = useCallback(async (nomenclatures) => {
    if (nomenclatures.length === 0) return {};
    const uniqueNomenclatures = [...new Set(nomenclatures)];
    try {
      console.log(`[fetchCategoriesFromBitrix] Запрос категорий из Bitrix для ${uniqueNomenclatures.length} товаров`);
      const categories = {};
      const categoryPromises = uniqueNomenclatures.map(async (nomenclature) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/get-nomenclature-from-bitrix?nomenclature=${encodeURIComponent(nomenclature)}`
          );
          const result = await response.json();
          if (result.success && result.data && result.data.category) {
            categories[nomenclature] = result.data.category;
            console.log(`[fetchCategoriesFromBitrix] ✅ Найдена категория для "${nomenclature}": ${result.data.category}`);
          } else {
            categories[nomenclature] = '';
            console.log(`[fetchCategoriesFromBitrix] ⚠️ Категория не найдена для "${nomenclature}"`);
          }
        } catch (err) {
          console.error(`[fetchCategoriesFromBitrix] ❌ Ошибка для "${nomenclature}":`, err);
          categories[nomenclature] = '';
        }
      });
      await Promise.all(categoryPromises);
      console.log(`[fetchCategoriesFromBitrix] Итоговый результат:`, categories);
      return categories;
    } catch (err) {
      console.error('[fetchCategoriesFromBitrix] Ошибка при получении категорий из Bitrix:', err);
      return {};
    }
  }, []);

  // Функция конвертации названия региона в внутренний ID для API category-mappings
  // ВАЖНО: API использует внутренние ID (1, 2, 3), а не Bitrix ID (420, 421, 568)
  const getRegionIdByName = (regionName) => {
    if (!regionName) return '1'; // СПБ по умолчанию
    const name = regionName.toUpperCase();
    if (name.includes('СПБ') || name.includes('САНКТ') || name.includes('ПЕТЕРБУРГ')) return '1';
    if (name.includes('МСК') || name.includes('МОСКВ')) return '2';
    if (name.includes('КРД') || name.includes('КРАСНОДАР')) return '3';
    return '1';
  };

  // Функция получения материала и тега из БД
  const getMaterialAndTagByCategory = async (categoryName, regionName) => {
    if (!categoryName) return null;
    try {
      const regionId = getRegionIdByName(regionName);
      console.log(`[getMaterialAndTagByCategory] Ищем материал и тег для категории "${categoryName}" в регионе "${regionName}" (ID: ${regionId})`);
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/category-mappings?category=${encodeURIComponent(categoryName)}&regionId=${regionId}`
      );
      const result = await response.json();
      if (result && result.success && result.data) {
        console.log(`[getMaterialAndTagByCategory] ✅ Найдено:`, result.data);
        return result.data;
      } else {
        console.log(`[getMaterialAndTagByCategory] ❌ Ничего не найдено для категории "${categoryName}"`);
        return null;
      }
    } catch (error) {
      console.error('[getMaterialAndTagByCategory] Ошибка:', error);
      return null;
    }
  };

  // Поиск поддепартамента по материалу (вспомогательная функция)
  function findSubDepartmentByMaterial(dept, terms) {
    for (const term of terms) {
      if (dept.NAME && dept.NAME.toLowerCase().includes(term)) {
        return dept;
      }
    }
    if (dept.subDepartments && dept.subDepartments.length > 0) {
      for (const subDept of dept.subDepartments) {
        const found = findSubDepartmentByMaterial(subDept, terms);
        if (found) return found;
      }
    }
    return null;
  }

  // Поиск поддепартамента по материалу во всех департаментах дерева
  const findSubDepartmentInAllDepartments = (deptArr, searchTerms) => {
    for (const dept of deptArr) {
      if (dept.subDepartments && dept.subDepartments.length > 0) {
        for (const subDept of dept.subDepartments) {
          const found = findSubDepartmentByMaterial(subDept, searchTerms);
          if (found) return found;
        }
      }
    }
    return null;
  };

  // Поиск сотрудника в department-tree по региону и материалу/тегу (fallback)
  const findEmployeeInDepartmentTree = (departmentTree, regionName, materialName, tagName) => {
    if (!departmentTree) {
      console.log('[findEmployee] Нет данных department-tree');
      return null;
    }

    // Проверяем структуру department-tree
    const structure = Array.isArray(departmentTree)
      ? departmentTree
      : (departmentTree?.structure || [departmentTree]);
    if (structure.length === 0) {
      console.log('[findEmployee] Department-tree пуст');
      return null;
    }

    const regionToDepartment = {
      'Санкт-Петербург': ['Департамент ЛОГ ОС', 'Департамент ЛОГ НЦ', 'ЛОГ ОС', 'ЛОГ НЦ', 'ОС'],
      'СПБ': ['Департамент ЛОГ ОС', 'Департамент ЛОГ НЦ', 'ЛОГ ОС', 'ЛОГ НЦ', 'ОС'],
      'Москва': ['ЛОГ Московский филиал ОС', 'ЛОГ Московский филиал НЦ', 'МСК'],
      'МСК': ['ЛОГ Московский филиал ОС', 'ЛОГ Московский филиал НЦ', 'МСК'],
      'Краснодар': ['Департамент ЛОГ Краснодар', 'ЛОГ Краснодар', 'КРД'],
      'КРД': ['Департамент ЛОГ Краснодар', 'ЛОГ Краснодар', 'КРД'],
    };

    const materialToDepartment = {
      'утеплитель': ['Теплоизоляция', 'Утеплитель'],
      'теплоизоляция': ['Теплоизоляция', 'Утеплитель'],
      'кровля': ['Кровля, профлист', 'кровля'],
      'газобетон': ['Газобетон', 'газобетон'],
      'кирпич': ['Кирпич', 'кирпич'],
      'бетон': ['Бетон', 'бетон'],
      'общестрой': ['Общестрой', 'Общестрой'],
      'нерудка': ['Нерудка', 'Нерудка 2', 'мск_Нерудка', 'нерудка'],
      'металл': ['Металл', 'металл'],
    };

    const searchRegionAliases = regionToDepartment[regionName] || [regionName];

    const findDepartmentByRegion = (dept, regionAliases) => {
      for (const alias of regionAliases) {
        if (dept.NAME && dept.NAME.toLowerCase().includes(alias.toLowerCase())) {
          return dept;
        }
      }
      if (dept.subDepartments && dept.subDepartments.length > 0) {
        for (const subDept of dept.subDepartments) {
          const found = findDepartmentByRegion(subDept, regionAliases);
          if (found) return found;
        }
      }
      return null;
    };

    let regionDepartment = null;
    for (const dept of structure) {
      regionDepartment = findDepartmentByRegion(dept, searchRegionAliases);
      if (regionDepartment) break;
    }

    if (!regionDepartment) {
      regionDepartment = structure[0];
      if (!regionDepartment) return null;
    }

    const searchTerms = [materialName.toLowerCase()];
    if (tagName) {
      searchTerms.push(tagName.toLowerCase());
      const synonyms = materialToDepartment[tagName.toLowerCase()];
      if (synonyms) searchTerms.push(...synonyms.map(s => s.toLowerCase()));
    }

    const materialDepartment = findSubDepartmentByMaterial(regionDepartment, searchTerms);

    if (!materialDepartment) {
      const globalMaterialDepartment = findSubDepartmentInAllDepartments(structure, searchTerms);
      if (globalMaterialDepartment && globalMaterialDepartment.employees && globalMaterialDepartment.employees.length > 0) {
        return globalMaterialDepartment.employees[0];
      }
      if (regionDepartment.employees && regionDepartment.employees.length > 0) {
        return regionDepartment.employees[0];
      }
      return null;
    }

    if (materialDepartment.employees && materialDepartment.employees.length > 0) {
      return materialDepartment.employees[0];
    }

    if (regionDepartment.employees && regionDepartment.employees.length > 0) {
      return regionDepartment.employees[0];
    }

    return null;
  };

  // Функция для определения нужного flowId на основе материала
  const findFlowIdByMaterial = (item, flowsArr) => {
    if (!flowsArr || flowsArr.length === 0) return 25; // Fallback

    const materialName = item?.materialFromDB || item?.material || item?.tag || '';
    const materialNameLower = materialName.toLowerCase();

    let flow = flowsArr.find(f =>
      f.name.toLowerCase() === materialNameLower
    );

    if (!flow) {
      flow = flowsArr.find(f => {
        const flowNameLower = f.name.toLowerCase();
        return materialNameLower.includes(flowNameLower) || flowNameLower.includes(materialNameLower);
      });
    }

    if (!flow) {
      const keywordMap = [
        { keywords: ['бетон', 'жби'], flowName: 'бетон' },
        { keywords: ['газобетон'], flowName: 'газобетон' },
        { keywords: ['кирпич', 'стеновые'], flowName: 'кирпич' },
        { keywords: ['кровля', 'профлист', 'фасад', 'забор'], flowName: 'кровля' },
        { keywords: ['металл'], flowName: 'металл' },
        { keywords: ['теплоизоляция', 'утеплитель'], flowName: 'теплоизоляция' },
        { keywords: ['нерудка', 'щебень', 'песок'], flowName: 'нерудка' },
      ];

      for (const { keywords, flowName } of keywordMap) {
        if (keywords.some(keyword => materialNameLower.includes(keyword))) {
          flow = flowsArr.find(f => f.name.toLowerCase().includes(flowName));
          if (flow) break;
        }
      }
    }

    if (flow) {
      return flow.id;
    } else {
      return 25;
    }
  };

  // Функция открытия модального окна логиста
  const handleOpenLogistRequestModal = useCallback(async (entry) => {
    try {
      console.log('🚀 Открываем модальное окно логиста', { dealId });
      console.log('[handleOpenLogistRequestModal] Entry:', entry);

      // Загружаем paymentType из сделки
      if (dealId) {
        try {
          const dealResponse = await fetch(
            `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
            { credentials: "include" }
          );
          const dealResult = await dealResponse.json();
          if (dealResult.success && dealResult.data) {
            const categoryId = parseInt(dealResult.data.CATEGORY_ID, 10);
            const categoryToPaymentType = {
              0: 'Нал',      // Частные лица
              1: 'Безнал',   // Юридические лица
              2: 'Нал',      // Повторные продажи ЧЛ
              3: 'Безнал'    // Повторные продажи ЮЛ
            };
            const determinedPaymentType = categoryToPaymentType[categoryId] || 'Нал';
            setPaymentType(determinedPaymentType);
            console.log(`💳 Форма оплаты по воронке CATEGORY_ID=${categoryId}: ${determinedPaymentType}`);
          }
        } catch (e) {
          console.error('Ошибка получения paymentType:', e);
        }
      }

      // Загружаем данные для dropdown'ов
      await preloadLogistData();

      // Получаем все материалы из записи (без фильтра по дате)
      const materialsToShow = entry.materialsToShow || [];
      console.log(`[handleOpenLogistRequestModal] Материалов в записи: ${materialsToShow.length}`, materialsToShow);

      if (materialsToShow.length === 0) {
        console.log('Нет материалов для отправки');
        return;
      }

      // Используем регион из logistFormData (который пользователь выбрал в модалке), или из пропсов
      // selectedRegion берется из сделки Битрикса и не меняется при выборе в dropdown!
      const shortRegionName = getShortRegionName(logistFormData.regionName) || selectedRegion || 'СПБ';
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || 'Санкт-Петербург';

      // Находим соответствующий regionOptions
      const regionOption = regionOptions.find(opt =>
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      const regionValue = regionOption?.value || '';

      console.log(`[handleOpenLogistRequestModal] Определенный регион: ${shortRegionName} -> ${fullRegionName} (value: ${regionValue})`);

      // Получаем категории из Bitrix
      const uniqueNomenclatures = [...new Set(materialsToShow.map(m => m.name).filter(n => n))];
      console.log(`[handleOpenLogistRequestModal] Уникальных номенклатур: ${uniqueNomenclatures.length}`, uniqueNomenclatures);

      const categoriesFromBitrix = await fetchCategoriesFromBitrix(uniqueNomenclatures);
      console.log(`[handleOpenLogistRequestModal] Категории из Bitrix:`, categoriesFromBitrix);

      // Получаем department-tree
      let currentDepartmentTree = departmentTree;
      if (!currentDepartmentTree) {
        try {
          const cachedTree = localStorage.getItem('departmentTree_15');
          const cachedTimestamp = localStorage.getItem('departmentTree_15_timestamp');
          const CACHE_TTL = 10 * 60 * 1000;

          if (cachedTree && cachedTimestamp) {
            const isExpired = Date.now() - parseInt(cachedTimestamp) > CACHE_TTL;
            if (!isExpired) {
              const parsedTree = JSON.parse(cachedTree);
              if (parsedTree && !Array.isArray(parsedTree) && parsedTree.structure) {
                currentDepartmentTree = parsedTree.structure;
              } else if (Array.isArray(parsedTree)) {
                currentDepartmentTree = parsedTree;
              } else if (parsedTree && parsedTree.ID) {
                currentDepartmentTree = [parsedTree];
              } else {
                currentDepartmentTree = parsedTree;
              }
              setDepartmentTree(currentDepartmentTree);
            } else {
              localStorage.removeItem('departmentTree_15');
              localStorage.removeItem('departmentTree_15_timestamp');
            }
          }

          if (!currentDepartmentTree) {
            const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/department-tree/15`, {
              credentials: "include",
            });
            const result = await response.json();
            if (result.success) {
              currentDepartmentTree = result.structure;
              setDepartmentTree(result.structure);
              localStorage.setItem('departmentTree_15', JSON.stringify(currentDepartmentTree));
              localStorage.setItem('departmentTree_15_timestamp', Date.now().toString());
            }
          }
        } catch (err) {
          console.error('[handleOpenLogistRequestModal] Ошибка при загрузке department-tree:', err);
        }
      }

      // Подготавливаем данные для формы
      const itemsPromises = materialsToShow.map(async (material) => {
        const nomenclature = material.name || '';
        const categoryFromBitrix = categoriesFromBitrix[nomenclature] || material.category || material.calculator || '';

        // Получаем материал и тег из БД (основной источник)
        const materialTagInfo = await getMaterialAndTagByCategory(categoryFromBitrix, shortRegionName);

        // Используем тег из БД, если есть, иначе fallback на getTagByCategory
        const tagFromDB = materialTagInfo?.tag || getTagByCategory(categoryFromBitrix);
        const materialFromDB = materialTagInfo?.material || '';

        // Определяем сотрудника через department-tree
        let employee = null;
        if (currentDepartmentTree) {
          employee = findEmployeeInDepartmentTree(
            currentDepartmentTree,
            shortRegionName,
            materialFromDB || nomenclature,
            tagFromDB
          );
        }

        console.log(`[handleOpenLogistRequestModal] Товар "${nomenclature}": category=${categoryFromBitrix}, tag=${tagFromDB}, material=${materialFromDB}, employee=`, employee);

        return {
          id: 0,
          material: nomenclature,
          nomenclature: nomenclature,
          category: categoryFromBitrix,
          tagId: tagFromDB,
          materialFromDB: materialFromDB,
          materialId: materialTagInfo?.materialId || '',
          materialBitrixId: materialTagInfo?.materialBitrixId || material.bitrixId || '',
          tagIdFromDB: materialTagInfo?.tag || '',
          departmentId: materialTagInfo?.departmentId || '',
          groupId: materialTagInfo?.groupId || '',
          employee: employee,
          quantity: material.quantity || 1,
          unit: material.unit || material.measure || 'шт',
          measureCode: material.measureCode || '796',
          price: material.pricePerOne || '',
          competitorPrice: '',
          comment: ``
        };
      });

      const items = await Promise.all(itemsPromises);
      let idCounter = 1;
      items.forEach(item => {
        item.id = idCounter++;
      });

      setLogistFormData({
        entry: entry,  // Сохраняем весь entry
        regionId: regionValue,
        regionName: fullRegionName,
        comment: '',
        _lastItemsRegion: shortRegionName,
        items: items
      });

      setShowLogistRequestModal(true);
    } catch (error) {
      console.error('Ошибка при открытии модального окна логиста:', error);
    }
  }, [regionOptions, fetchCategoriesFromBitrix, departmentTree, preloadLogistData, selectedRegion, dealId, setPaymentType]);

  // Отслеживаем изменение состояния загрузки
  useEffect(() => {
    if (!isLoading && sortedGroupedData.length > 0 && !showTooltipOnFirstLoad) {
      setShowTooltipOnFirstLoad(true);
    }
  }, [isLoading, sortedGroupedData.length, showTooltipOnFirstLoad]);

  // Устанавливаем регион после загрузки regionOptions
  useEffect(() => {
    if (regionOptions.length > 0 && showLogistRequestModal && !logistFormData.regionId) {
      const shortRegionName = selectedRegion || 'СПБ';
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || 'Санкт-Петербург';

      const regionOption = regionOptions.find(opt =>
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      if (regionOption) {
        setLogistFormData(prev => ({
          ...prev,
          regionId: regionOption.value,
          regionName: regionOption.label
        }));
        console.log('[useEffect] Регион установлен:', regionOption.label);
      }
    }
  }, [regionOptions, showLogistRequestModal, logistFormData.regionId, selectedRegion]);

  // ================================================================
  // Пересчитываем items при изменении региона в dropdown
  // ================================================================
  useEffect(() => {
    // Пересчитываем только если модалка открыта, есть regionId, но items ещё не пересчитаны для этого региона
    if (!showLogistRequestModal || !logistFormData.regionId) return;

    // Проверяем, нужно ли пересчитать items (если items есть, но regionId изменился)
    const currentRegionId = logistFormData.regionId;
    const needsRecalculation = !logistFormData.items?.length ||
      (logistFormData.regionName !== logistFormData._lastItemsRegion);

    if (!needsRecalculation) return;

    const recalculateItems = async () => {
      console.log('[useEffect] Пересчитываем items для региона:', logistFormData.regionName);

      const shortRegionName = getShortRegionName(logistFormData.regionName) || selectedRegion || 'СПБ';
      const fullRegionName = REGION_SHORT_TO_FULL[shortRegionName] || logistFormData.regionName || 'Санкт-Петербург';

      // Находим regionOption для текущего региона
      const regionOption = regionOptions.find(opt =>
        opt.value === logistFormData.regionId ||
        opt.label === fullRegionName ||
        opt.label === shortRegionName ||
        (opt.label.includes('Санкт') && shortRegionName === 'СПБ') ||
        (opt.label.includes('Москв') && shortRegionName === 'МСК') ||
        (opt.label.includes('Краснодар') && shortRegionName === 'КРД')
      );

      const regionValue = regionOption?.value || logistFormData.regionId;

      try {
        // Получаем categories из Bitrix
        const entry = logistFormData.entry;
        const materialsToShow = entry?.materialsToShow || [];
        const uniqueNomenclatures = [...new Set(materialsToShow.map(m => m.name).filter(n => n))];
        const categoriesFromBitrix = await fetchCategoriesFromBitrix(uniqueNomenclatures);

        // Формируем items с правильным regionId
        const itemsPromises = materialsToShow.map(async (material) => {
          const nomenclature = material.name || '';
          const categoryFromBitrix = categoriesFromBitrix[nomenclature] || material.category || material.calculator || '';
          const materialTagInfo = await getMaterialAndTagByCategory(categoryFromBitrix, shortRegionName);
          const tagFromDB = materialTagInfo?.tag || getTagByCategory(categoryFromBitrix);
          const materialFromDB = materialTagInfo?.material || '';

          return {
            id: 0,
            material: nomenclature,
            nomenclature: nomenclature,
            category: categoryFromBitrix,
            tagId: tagFromDB,
            materialFromDB: materialFromDB,
            materialId: materialTagInfo?.materialId || '',
            materialBitrixId: materialTagInfo?.materialBitrixId || material.bitrixId || '',
            tagIdFromDB: materialTagInfo?.tag || '',
            departmentId: materialTagInfo?.departmentId || '',
            groupId: materialTagInfo?.groupId || '',
            employee: null,
            quantity: material.quantity || 1,
            unit: material.unit || material.measure || 'шт',
            measureCode: material.measureCode || '796',
            price: material.pricePerOne || '',
            competitorPrice: '',
            comment: ``
          };
        });

        const items = await Promise.all(itemsPromises);
        let idCounter = 1;
        items.forEach(item => { item.id = idCounter++; });

        console.log('[useEffect] items пересчитаны:', items);

        setLogistFormData(prev => ({
          ...prev,
          regionId: regionValue,
          regionName: fullRegionName,
          _lastItemsRegion: shortRegionName,
          items: items
        }));
      } catch (error) {
        console.error('[useEffect] Ошибка при пересчёте items:', error);
      }
    };

    recalculateItems();
  }, [showLogistRequestModal, logistFormData.regionId, logistFormData.regionName, selectedRegion, regionOptions, fetchCategoriesFromBitrix]);

  // Лучшая цена — минимальная общая сумма среди строк с реальным итогом.
  // Считается только внутри МСК Газобетона: вне области подсветки нет.
  const bestEntryId = isMskGazobetonScope
    ? (Array.isArray(sortedGroupedData) ? sortedGroupedData : []).reduce((best, entry) => {
      const total = Number(entry?.totalSum);
      if (!(total > 0)) return best;
      return !best || total < Number(best.totalSum) ? entry : best;
    }, null)?.id ?? null
    : null;

  // Определяем, нужно ли показывать колонку "Сколько дней цене" (скрываем для "Бетон")
  const hasDaysColumn = !sortedGroupedData.length ||
    !sortedGroupedData.every(entry =>
      entry.materialsToShow[0]?.calculator === "Бетон"
    );

  // Таблица сравнения внутри МСК Газобетона — своя разметка. Базовая таблица ниже
  // не трогается: она продолжает рисоваться вне области и в тех редких состояниях,
  // где у области нет собственного вида (регламент, «Общестрой», загрузка, пусто).
  const gazComparisonView = buildGazComparisonView({
    rows: sortedGroupedData,
    paymentMethod,
    selectedComparisonId,
    isMskGazobetonScope,
  });
  const showGazComparison = isMskGazobetonScope
    && !hasObshchestroyCalculator
    && isValidInput
    && !isLoading
    && gazComparisonView.rows.length > 0
    && (Array.isArray(sortedGroupedData) ? sortedGroupedData : []).every((entry) => !entry?.isReagent);

  // Динамическая grid-сетка под видимые колонки таблицы.
  const prodGridColumns = [
    "22px",                               // №
    "minmax(0, 1.1fr)",                  // Производство
    "minmax(0, 0.85fr)",                 // Сумма материалов
    !selfPickUp ? "minmax(0, 0.85fr)" : null,  // Сумма доставки
    !selfPickUp ? "minmax(0, 0.9fr)" : null,   // Общая сумма
    hasDaysColumn ? "minmax(0, 0.55fr)" : null, // Сколько дней цене
    "minmax(0, 0.85fr)",                 // Источник
    hasValidDescriptions ? "minmax(0, 0.8fr)" : null, // Описание
  ].filter(Boolean).join(" ");

  if (showGazComparison) {
    return (
      <div ref={tableContainerRef} className="rd-prod-wrap">
        <GazComparisonTable
          view={gazComparisonView}
          selfPickUp={selfPickUp}
          hasUpsells={hasGazUpsells}
          onSelect={(comparison, entry) => handleSelectOffer(comparison, entry, true)}
          /* Фото берётся тем же путём, что и в общей таблице: наличие уже
             проверено разом по всем строкам, модалка отрисована ниже. */
          hasPhoto={(row) => Boolean(imageAvailabilityMap[getImageAvailabilityKey(row?.entry)])}
          onOpenPhoto={(row, event) => handleOpenImageModal(row?.entry, event)}
          onProductionMouseEnter={(event, row) => handleMouseEnter(
            event,
            row?.comparison?.productionInfo?.ADDITIONAL_INFO?.toLocaleString()
          )}
          onProductionMouseLeave={handleMouseLeave}
        />

        {/* Подсказка позиционируется относительно .rd-prod-wrap — так же, как у
            базовой таблицы: координаты считает общий useTooltipLogic. */}
        {tooltip.visible && tooltip.text && (
          <div
            ref={tooltipRef}
            style={{
              position: "absolute",
              top: `${tooltip.y}px`,
              left: `${tooltip.x}px`,
              background: "#ffffff",
              color: "#020217",
              padding: "11px 14px",
              border: "1px solid #dbe3ef",
              borderRadius: "10px",
              zIndex: 1000,
              maxWidth: "300px",
              fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              boxShadow:
                "0 1px 2px rgba(2,2,23,.06), 0 16px 36px -14px rgba(2,2,23,.32)",
              pointerEvents: "none",
            }}
            dangerouslySetInnerHTML={{ __html: tooltip.text?.replace(/\n/g, "<br/>") }}
          />
        )}

        {/* Те же модалки, что и у базовой ветки: их состояние живёт в этом
            компоненте, и терять его при смене вида таблицы нельзя. */}
        <LogistRequestModal
          show={showLogistRequestModal}
          onHide={handleCloseLogistRequestModal}
          logistFormData={logistFormData}
          logistFormErrors={logistFormErrors}
          handleItemChange={handleItemChange}
          validateLogistForm={validateLogistForm}
          regionOptions={regionOptions}
          isLoadingLogistOptions={isLoadingLogistOptions}
          setLogistFormData={setLogistFormData}
          dealId={dealId}
          clientName={clientName}
          addressAndCoordinates={addressAndCoordinates}
          flows={flows}
          findFlowIdByMaterial={findFlowIdByMaterial}
          deliveryDate={deliveryDate}
          paymentType={paymentType}
        />
        <NomenclatureImagesModal
          isOpen={imageModalData.isOpen}
          title={imageModalData.title}
          imageUrls={imageModalData.imageUrls}
          isLoading={imageModalData.isLoading}
          onClose={handleCloseImageModal}
        />
      </div>
    );
  }

  return (
    <div ref={tableContainerRef} className="rd-prod-wrap">
      <table
        className="rd-prod-table"
        style={{ "--rd-prod-cols": prodGridColumns }}
      >
        <thead>
          <tr>
            <th scope="col">№</th>
            <th scope="col" className="rd-prod-th--text">Производство</th>
            <th scope="col">Сумма материалов</th>
            {!selfPickUp && <th scope="col">Сумма доставки</th>}
            {!selfPickUp && <th scope="col">Общая сумма</th>}
            {hasDaysColumn && <th scope="col">Дней цене</th>}
            <th scope="col" className="rd-prod-th--text">Источник</th>
            {hasValidDescriptions && <th scope="col" className="rd-prod-th--text">Описание</th>}
          </tr>
        </thead>
        <tbody>
          {!isValidInput ? (
            <tr>
              {hasObshchestroyCalculator ? (
                <>
                  {/* Ячейка с "регламент" */}
                  <td
                    colSpan={3}
                    style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
                  >
                    <span
                      className="rd-prod-regulation-link"
                      onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                    >
                      регламент
                    </span>
                  </td>
                  {/* Ячейка с кнопкой "Отправить запрос" */}
                  <td
                    colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6)) - 3}
                    style={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    {hasValidDescriptions ? (
                      <div>
                        <span
                          className="rd-prod-regulation-link"
                          onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                        >
                          регламент
                        </span>
                        <br /><br />
                        <button
                          onClick={() => {
                            handleOpenLogistRequestModal(createEntryFromData());
                          }}
                          className="rd-prod-request-btn"
                        >
                          Отправить запрос
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          handleOpenLogistRequestModal(createEntryFromData());
                        }}
                        className="rd-prod-request-btn"
                      >
                        Отправить запрос
                      </button>
                    )}
                  </td>
                </>
              ) : (
                <td
                  colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6))}
                  className="text-center"
                >
                  "Нет данных"
                </td>
              )}
            </tr>
          ) : isLoading ? (
            <tr>
              <td
                colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6))}
                className="text-center"
              >
                {isCatMode ? (
                  <CatLoaderInline />
                ) : (
                  <>
                    <span>Загрузка...</span>{" "}
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </>
                )}
              </td>
            </tr>
          ) : shouldShowRequestLink ? (
            <tr>
              {/* Ячейка с "регламент" */}
              <td
                colSpan={3}
                style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
              >
                <span
                  className="rd-prod-regulation-link"
                  onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                >
                  регламент
                </span>
              </td>
              {/* Ячейка с кнопкой "Отправить запрос" */}
              <td
                colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6)) - 3}
                style={{ textAlign: "center", verticalAlign: "middle" }}
              >
                {(() => {
                  return hasValidDescriptions ? (
                    <div>
                      <button
                        onClick={() => handleOpenLogistRequestModal(createEntryFromData())}
                        className="rd-prod-request-btn"
                      >
                        Отправить запрос
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleOpenLogistRequestModal(createEntryFromData())}
                      className="rd-prod-request-btn"
                    >
                      Отправить запрос
                    </button>
                  );
                })()}
              </td>
            </tr>
          ) : sortedGroupedData.length > 0 && sortedGroupedData.every(entry => entry.comparison.production === "Из справочника") ? (
            // Если все записи имеют manufacturer = "Из справочника", то показываем "регламент" и кнопку "Отправить запрос"
            <tr>
              {/* Ячейка с "регламент" */}
              <td
                colSpan={3}
                style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
              >
                <span
                  className="rd-prod-regulation-link"
                  onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                >
                  регламент
                </span>
              </td>
              {/* Ячейка с кнопкой "Отправить запрос" */}
              <td
                colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6)) - 3}
                style={{ textAlign: "center", verticalAlign: "middle" }}
              >
                {(() => {
                  // Получаем запись для модалки
                  const entry = sortedGroupedData.find(e => e.comparison.production === "Из справочника");
                  return hasValidDescriptions ? (
                    <div>
                      <span style={{ fontSize: "19px" }}>регламент</span>
                      <br /><br />
                      <button
                        onClick={() => handleOpenLogistRequestModal(entry)}
                        className="rd-prod-request-btn"
                      >
                        Отправить запрос
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleOpenLogistRequestModal(entry)}
                      className="rd-prod-request-btn"
                    >
                      Отправить запрос
                    </button>
                  );
                })()}
              </td>
            </tr>
          ) : sortedGroupedData.length > 0 ? (
            sortedGroupedData.map((entry, index) => {
              const { comparison, material, materialsToShow, totalMaterialCostForDisplay, totalSum, isReagent, hasDeliveryError, deliveryError, isIzolifeEntry, izolifeDeliveryType, izolifePalletPrice, izolifeComment, izolifePerPalletsComment, izolifePalletsComment, izolifePhrase } = entry;
              if (materialsToShow[0].calculator === "Бетон") {
                materialsToShow[0].quantity = entry.deliveryForTooltip?.mix?.details?.data?.deliveredVolume;
              }

              // Собираем все уникальные описания из materialsToShow
              const uniqueDescriptions = Array.from(
                new Set(
                  materialsToShow
                    .map(m => m.description)
                    .filter(desc => desc && desc !== "Нет данных" && desc !== "-")
                )
              );
              const description =
                uniqueDescriptions.length > 0
                  ? uniqueDescriptions.join(", ") // Разделяем описания запятыми
                  : "—";

              // Собираем все уникальные источники из materialsToShow
              const uniqueSources = Array.from(
                new Set(
                  materialsToShow
                    .map(m => m.source)
                    .filter(source => source && source !== "-")
                )
              );
              const allSources = uniqueSources.length > 0 ? uniqueSources.join(", ") : "-";
              const imagePayload = getRowImagePayload(entry);
              const imageAvailabilityKey = getImageAvailabilityKey(entry);
              const hasNomenclatureImage = Boolean(
                imagePayload && imageAvailabilityMap[imageAvailabilityKey]
              );

              // Метки строки живут только внутри МСК Газобетона. Лучшая цена и выбранный
              // вариант — независимые состояния: выбранное производство не обязано быть
              // самым дешёвым. Выбранное определяется по ключу сравнения (имени
              // производителя): он не зависит от позиции строки и сортировки, а сравнения
              // строятся по одному на производителя — поэтому отметиться может ровно одна
              // строка. Если выбранного производства в результатах больше нет, не
              // совпадёт ни одна строка, и сработает автовыбор в useProductionsLogic.
              // Фактическая номенклатура этой строки сравнения: то, что производство
              // реально предлагает и по чему посчитана его сумма. Берётся из уже
              // готового entry.materialsToShow, дополнительного расчёта нет.
              const gazEntryNomenclature = isMskGazobetonScope
                ? Array.from(new Set(
                  (Array.isArray(materialsToShow) ? materialsToShow : [])
                    .map((material) => String(material?.name || '').trim())
                    .filter(Boolean)
                ))
                : [];

              const isBestPriceEntry = isMskGazobetonScope && entry.id === bestEntryId;
              const isSelectedEntry = isSelectedComparisonRow({
                isMskGazobetonScope,
                comparison,
                selectedComparisonId,
              });
              const rowStyle = isSelectedEntry
                ? { background: '#eaf2fb', boxShadow: 'inset 3px 0 0 #1976d2' }
                : undefined;

              return (
                <tr key={entry.id} style={rowStyle}>
                  <td>
                    <span className="rd-prod-num">{index + 1}</span>
                  </td>
                  <td
                    onMouseEnter={(e) =>
                      handleMouseEnter(e, comparison.productionInfo.ADDITIONAL_INFO?.toLocaleString())
                    }
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelectOffer(comparison, entry, true)}
                  >
                    {(isBestPriceEntry || isSelectedEntry) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                        {isBestPriceEntry && (
                          <span
                            style={{
                              padding: '1px 7px',
                              borderRadius: 4,
                              background: '#e8f5e9',
                              color: '#2e7d32',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ↓ Лучшая цена
                          </span>
                        )}
                        {isSelectedEntry && (
                          <span
                            style={{
                              padding: '1px 7px',
                              borderRadius: 4,
                              background: '#eaf2fb',
                              color: '#1976d2',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ✓ Выбрано
                          </span>
                        )}
                      </div>
                    )}
                    <div className="rd-prod-name" style={{ position: "relative" }}>
                      {hasNomenclatureImage && (
                        <button
                          type="button"
                          onClick={(event) => handleOpenImageModal(entry, event)}
                          title="Показать изображение"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "22px",
                            height: "22px",
                            border: "1px solid #dbe3ef",
                            borderRadius: "6px",
                            background: "#f6faff",
                            color: "#005ca9",
                            cursor: "pointer",
                            padding: 0,
                            marginRight: "6px",
                            flexShrink: 0,
                          }}
                        >
                          <Image size={12} />
                        </button>
                      )}
                      <span className="rd-prod-name__primary">{comparison.production}</span>
                    </div>
                    {/* По какому именно газобетону посчитана цена этой строки. Имена
                        берутся из materialsToShow самого entry — это фактическая
                        номенклатура производства (NOMENCLATURE), по которой и считалась
                        сумма материалов. Ничего не достраивается и не подставляется от
                        других строк. Только внутри МСК Газобетона. */}
                    {isMskGazobetonScope && gazEntryNomenclature.length > 0 && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 11,
                          lineHeight: 1.35,
                          color: '#78909c',
                          whiteSpace: 'normal',
                        }}
                      >
                        {gazEntryNomenclature.join(' · ')}
                      </div>
                    )}
                    {isMskGazobetonScope && !isSelectedEntry && !isReagent && (
                      <div style={{ marginTop: 4 }}>
                        <span
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleSelectOffer(comparison, entry, true);
                            }
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectOffer(comparison, entry, true);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            borderRadius: 4,
                            border: '1px solid #1976d2',
                            color: '#1976d2',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            background: '#ffffff',
                          }}
                        >
                          Выбрать
                        </span>
                      </div>
                    )}
                    {hasDeliveryError && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "15px",
                          lineHeight: 1.35,
                          color: "#c62828",
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                      >
                        Ошибка доставки
                      </div>
                    )}
                  </td>

                  {/* Если это запись "регламент", разделяем на ячейки */}
                  {isReagent ? (() => {
                    const reagentCols = selfPickUp ? 2 : 3;
                    // Когда есть колонка "Описание" — выносим её отдельно, уменьшаем buttonCols на 1
                    const buttonCols = hasValidDescriptions
                      ? (selfPickUp ? (hasDaysColumn ? 1 : 0) : (hasDaysColumn ? 2 : 1))
                      : (selfPickUp ? 1 : 2);

                    return (
                      <>
                        {/* Ячейка с "регламент" */}
                        <td
                          colSpan={reagentCols}
                          style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
                        >
                          <MaterialsDetailedTooltip
                            materials={materialsToShow}
                            autoShow={false}
                          >
                            <MaterialsTooltip
                              materials={materialsToShow}
                              deliveryForTooltip={entry.deliveryForTooltip}
                              isReagent={isReagent}
                              izolifeComment={izolifeComment}
                              izolifePerPalletsComment={izolifePerPalletsComment}
                              izolifePalletsComment={izolifePalletsComment}
                              izolifePhrase={izolifePhrase}
                            />
                          </MaterialsDetailedTooltip>
                        </td>
                        {/* Ячейка с кнопкой "Отправить запрос" */}
                        {buttonCols > 0 && (
                          <td
                            colSpan={buttonCols}
                            style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
                          >
                            <button
                              onClick={() => handleOpenLogistRequestModal(entry)}
                              className="rd-prod-request-btn"
                            >
                              Отправить запрос
                            </button>
                          </td>
                        )}
                        {/* Ячейка с описанием — отдельно, как у обычных строк */}
                        {hasValidDescriptions && (
                          <td className="rd-prod-td--text" style={{ verticalAlign: "middle", fontSize: "20px" }}>
                            {description}
                            {buttonCols === 0 && (
                              <div style={{ marginTop: "8px" }}>
                                <button
                                  onClick={() => handleOpenLogistRequestModal(entry)}
                                  className="rd-prod-request-btn"
                                >
                                  Отправить запрос
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </>
                    );
                  })() : (
                    <>
                      {/* Обычные ячейки для не-регламентных записей */}
                      <td style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }} >
                        <MaterialsDetailedTooltip
                          materials={materialsToShow}
                          autoShow={false}
                        >
                          <MaterialsTooltip
                            materials={materialsToShow}
                            deliveryForTooltip={entry.deliveryForTooltip}
                            isReagent={isReagent}
                            overrideTotalCost={isIzolifeEntry ? totalMaterialCostForDisplay : undefined}
                            izolifeComment={izolifeComment}
                            izolifePerPalletsComment={izolifePerPalletsComment}
                            izolifePalletsComment={izolifePalletsComment}
                            izolifePhrase={izolifePhrase}
                          />
                        </MaterialsDetailedTooltip>
                        {/* {isIzolifeEntry && izolifeComment && (
                          <div style={{ fontSize: '14px', color: '#c62828', fontWeight: 500, marginTop: 4 }}>
                            {izolifeComment}
                          </div>
                        )}
                        {isIzolifeEntry && izolifePhrase && (
                          <div style={{ fontSize: '14px', color: '#c62828', fontWeight: 500, marginTop: 2 }}>
                            {izolifePhrase}
                          </div>
                        )} */}
                      </td>
                      {!selfPickUp && (
                        <td style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }} >
                          {isIzolifeEntry ? (
                            izolifeDeliveryType === 'full_trucks' || !izolifePalletPrice ? (
                              <div>
                                <span style={{ color: 'red', fontWeight: 500, fontSize: '16px' }}>С учетом доставки</span>
                                <br />
                                <span style={{ color: 'red', fontWeight: 500, fontSize: '16px' }}>(ТРАНЗИТ — ФУРА)</span>
                              </div>
                            ) : (
                              <span>{parseInt(izolifePalletPrice).toLocaleString()}</span>
                            )
                          ) : hasDeliveryError ? (
                            <div style={{ color: "#c62828", fontSize: "16px", lineHeight: 1.35 }}>
                              {deliveryError || "Не удалось рассчитать доставку"}
                            </div>
                          ) : (
                            <DeliveryTooltip
                              delivery={entry.deliveryForTooltip}
                              categoriesToInclude={entry.categoriesToInclude}
                            />
                          )}
                        </td>
                      )}
                      {!selfPickUp && (
                        <td style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }} >
                          {hasDeliveryError
                            ? "Ошибка расчета"
                            : (totalSum > 0
                              ? (isBestPriceEntry
                                ? (
                                  <span style={{ color: '#2e7d32', fontWeight: 700 }}>
                                    {Math.ceil(totalSum).toLocaleString()}
                                  </span>
                                )
                                : Math.ceil(totalSum).toLocaleString())
                              : "Нет данных")}
                        </td>
                      )}
                      {hasDaysColumn && (
                        <td style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}>
                          <DaysTooltip materials={materialsToShow} />
                        </td>
                      )}
                      <td className="rd-prod-source rd-prod-td--text">{allSources}</td>
                      {hasValidDescriptions && <td className="rd-prod-td--text" style={{ verticalAlign: "middle", fontSize: "20px" }} >{description}</td>}
                    </>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              {hasObshchestroyCalculator ? (
                <>
                  {/* Ячейка с "регламент" */}
                  <td
                    colSpan={3}
                    style={{ textAlign: "center", verticalAlign: "middle", fontSize: "20px" }}
                  >
                    <span
                      className="rd-prod-regulation-link"
                      onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                    >
                      регламент
                    </span>
                  </td>
                  {/* Ячейка с кнопкой "Отправить запрос" */}
                  <td
                    colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6)) - 3}
                    style={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    {hasValidDescriptions ? (
                      <div>
                        <span
                          className="rd-prod-regulation-link"
                          onClick={() => window.open("https://knowledgebase.mlt-development.ru/material/585/view", "_blank")}
                        >
                          регламент
                        </span>
                        <br /><br />
                        <button
                          onClick={() => {
                            handleOpenLogistRequestModal(createEntryFromData());
                          }}
                          className="rd-prod-request-btn"
                        >
                          Отправить запрос
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          handleOpenLogistRequestModal(createEntryFromData());
                        }}
                        className="rd-prod-request-btn"
                      >
                        Отправить запрос
                      </button>
                    )}
                  </td>
                </>
              ) : (
                <td
                  colSpan={hasValidDescriptions ? (hasDaysColumn ? (selfPickUp ? 7 : 8) : (selfPickUp ? 6 : 7)) : (hasDaysColumn ? (selfPickUp ? 6 : 7) : (selfPickUp ? 5 : 6))}
                  className="text-center"
                >
                  "Нет данных"
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
      {tooltip.visible && tooltip.text && (
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            top: `${tooltip.y}px`,
            left: `${tooltip.x}px`,
            background: "#ffffff",
            color: "#020217",
            padding: "11px 14px",
            border: "1px solid #dbe3ef",
            borderRadius: "10px",
            zIndex: 1000,
            maxWidth: "300px",
            fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
            boxShadow:
              "0 1px 2px rgba(2,2,23,.06), 0 16px 36px -14px rgba(2,2,23,.32)",
            pointerEvents: "none",
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.text?.replace(/\n/g, "<br/>") }}
        />
      )}

      {/* Модальное окно запроса логисту */}
      <LogistRequestModal
        show={showLogistRequestModal}
        onHide={handleCloseLogistRequestModal}
        logistFormData={logistFormData}
        logistFormErrors={logistFormErrors}
        handleItemChange={handleItemChange}
        validateLogistForm={validateLogistForm}
        regionOptions={regionOptions}
        isLoadingLogistOptions={isLoadingLogistOptions}
        setLogistFormData={setLogistFormData}
        dealId={dealId}
        clientName={clientName}
        addressAndCoordinates={addressAndCoordinates}
        flows={flows}
        findFlowIdByMaterial={findFlowIdByMaterial}
        deliveryDate={deliveryDate}
        paymentType={paymentType}
      />
      <NomenclatureImagesModal
        isOpen={imageModalData.isOpen}
        title={imageModalData.title}
        imageUrls={imageModalData.imageUrls}
        isLoading={imageModalData.isLoading}
        onClose={handleCloseImageModal}
      />
    </div>
  );
};

export default ProductionsTable;
